import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Security & Governance',
  content: `
Security & Governance is where your Pliable administrators decide **who can see which data**. You can let a store manager see only their own store, keep salary figures away from people who shouldn't see them, or hide a metric entirely, and then check the result for yourself before anyone else sees it.

You'll find it in the left-hand menu under **Security & Governance**. Only administrators can open it.

For how restrictions behave everywhere, including embedded dashboards and API tokens, see [Data Restrictions](/docs/data-restrictions).

## The three building blocks

| | What it does | Example |
|---|---|---|
| **Groups** | A named set of rules shared by everyone in the group. Each person can be in one group. | "Store Managers", "Regional Finance", "Payroll Team" |
| **Row restrictions** | Limit *which rows* people see: only the stores, regions, people or other things they're allowed to see. | "Only show Store 301", "Don't show executives' pay" |
| **Metric visibility** | Hide a metric completely, so it doesn't appear in charts, dashboards or Ellie's answers. | Hide "Bonus Pool" from everyone outside Payroll |

Rules can be set on a group, on an individual person, or both. A person always gets **their group's rules plus their own**. Rules only ever narrow what someone sees; adding a rule never shows anyone more.

## Row restrictions, in plain terms

A row restriction is a sentence of the form **"only show rows where ___"**. For example:

- Only show rows where **Store** is **301**.
- Only show rows where **Region** is **North** or **South**.
- Only show rows where **Employee Type** is **not Executive**.

### Applying a restriction to all metrics, or only some

Often a restriction only matters for certain numbers. Take "don't show executives' pay":

- **Total Pay** should leave executives out.
- **Eggs Sold** at a store has nothing to do with who the executives are, and should still show the store's full total.

So each restriction asks what it **applies to**:

- **All metrics.** The restriction narrows every number. You also choose what happens to metrics that can't be broken down this way (see below).
- **Specific metrics.** The restriction only narrows the metrics you pick, such as Total Pay, and everything else is left as it is. Only metrics that can be broken down by what you're restricting on are offered.

### Metrics that "aren't connected"

Some metrics simply don't contain the information a restriction is about. "Eggs Sold" isn't recorded per employee, so it can't be split into executive and non-executive. In Pliable terms the metric **isn't connected** to that field. Usually that's because the breakdown doesn't make sense; sometimes it's because the connection hasn't been set up yet.

When a restriction applies to all metrics, you choose what happens to these:

- **Hide them** (the default). People see nothing for these metrics rather than a number the restriction doesn't cover.
- **Show them.** Choose this when you're sure they don't contain the restricted information, such as a company-wide total, so it's safe to show them in full.

The editor lists which metrics aren't connected, so you can see exactly what your choice affects.

### Watch out for calculated metrics

Some metrics are calculated from others. For example, **Margin = Revenue − Cost**. If a restriction covers Revenue but not Cost, Margin ends up subtracting *all* costs from *restricted* revenue, which gives a number that doesn't mean anything.

The editor flags any calculated metric like this with an amber warning, and names which of its parts are restricted and which aren't. To fix it, add the other parts to the restriction, or hide the calculated metric under Metric Visibility.

## Metric visibility

On the **Metric Visibility** tab, untick a metric to hide it from that group or person. Hidden metrics don't appear anywhere they look: dashboards, charts, the metric list, or Ellie's answers.

Hiding a metric also hides **every metric calculated from it**. If you hide "Bonus Pool", "Total Compensation = Salary + Bonus Pool" is hidden too, since otherwise the bonus could be worked out by subtraction.

Metrics added later are **visible by default**. If a new metric is sensitive, hide it from the groups that shouldn't see it as soon as it's published. The publish window reminds you (see *Making changes to your data model* below).

## Setting it up

1. Go to **Security & Governance → Groups** and click **New group**. Give it a name and a short description.
2. On **Row Restrictions**, add one or more restrictions. Restrictions combine, so "Region is North" plus "Employee Type is not Executive" means both must be true.
3. On **Metric Visibility**, untick anything the group shouldn't see.
4. Save, then go to **Users**, open each person, and choose their **Group**. You can add restrictions just for that person in the same window.

## Checking it does what you expect

You shouldn't have to take the rules on trust. Two tabs help:

- **Coverage** shows, for each group, what every restriction does to every metric: *Restricted*, *Not connected – shown*, *Not connected – hidden*, or *Hidden*. It also lists warnings, such as a restriction that points at something that no longer exists.
- **Preview access** lets you look through someone else's eyes. Pick a person or a group, then open a **dashboard**, a **chart**, or a conversation with **Ellie**. Everything on that tab uses *their* access, while the rest of Pliable still uses yours. You keep your own permissions; only what data you can see changes.

The first time you preview someone, their numbers may take a moment to prepare. That's normal.

## Making changes to your data model

If you use **Develop** mode to change metrics and fields before publishing them, access rules work like this:

- **Rules aren't tied to Develop or Production.** There's one set, and it applies to whatever is published.
- **Rules can only use published metrics and fields.** New ones from Develop appear in the editors marked **Unpublished**, but you can't select them until they're published. A rule can't protect anything in Production that only exists in Develop.
- **Changes in Develop never loosen a rule in Production.** Even if someone connects a metric differently in Develop, Production keeps applying (or refusing) exactly as before until you publish.
- **The editors warn you about upcoming problems.** If something in Develop would break a restriction (a metric removed, or a connection taken away), you'll see it marked, with a warning when you save.
- **Publishing asks you to review access changes.** If a publish would change what a restricted group can see, for example a metric becoming unrestricted or a new metric appearing for them, the Publish window lists it. You'll need to confirm you've reviewed it before publishing.
- **Preview can show either version.** Set **Data** to *Production* to see what people see today, or to *Develop* to see what they'll see once you publish.

## What restricted users see

- Charts, dashboards and Ellie's answers only include what they're allowed to see.
- If something is off-limits, Pliable says so rather than quietly showing a partial number. For example, a hidden metric simply isn't offered, and a metric a restriction can't be applied to shows an error rather than unrestricted data.
- **Goals** (targets set against metrics) aren't shown to anyone with restrictions or hidden metrics, because a goal can reveal both a number and the slice of data it's set for.
- If someone asks Ellie for access to more data, Ellie explains that access is managed by **your organization's Pliable administrators** in Security & Governance, so the request reaches the right people.

## Embedded dashboards and Ellie

If you embed Pliable dashboards or Ellie in your own product, the same row restrictions and metric visibility can be set for each embedded session, so your customers only see their own data. Your developers will find the details in [Embedding Pliable](/docs/embedding-pliable).

## Frequently asked questions

**Can someone be in more than one group?**
No, each person is in at most one group. Anything that applies only to that person can be added on their own record, on top of the group's rules.

**What happened to the old "Dimension Filters" on users?**
They still work exactly as before, and appear read-only on the person's Row Restrictions tab. You can convert them to row restrictions with one click, which also lets you choose what happens to metrics that aren't connected.

**Can a group give someone *more* access than their role allows?**
No. Roles decide what someone can *do* in Pliable (see [Roles](/docs/roles)). Groups and restrictions only narrow what *data* they see.

**Do restrictions work for Admins and Builders?**
Restrictions apply to their charts, dashboards and Ellie. But Admins and Builders can also read raw data and change the data model, so restrictions don't fully limit what they can see. Pliable warns you when you put an Admin or Builder in a restricted group. Give anyone who must be restricted the **ReadOnly** role.

**Who can change these settings?**
Only administrators. Everyone else, including people previewed through Preview access, can't see or change them.

**What if a restriction points at something that's been deleted?**
Pliable errs on the side of caution: the affected people get an error rather than unrestricted data, and Coverage shows a warning so you can fix the rule.
`,
};

export default doc;
